import React, { useState } from 'react';
import { Trash2, Plus, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const InventoryTab = ({ character, characterId, onUpdated }) => {
  const [newItem, setNewItem] = useState('');

  const inventory = character?.inventory || [];

  const updateInventory = async (newInventory) => {
    try {
      await axios.patch(
        `${API}/characters/${characterId}`,
        { inventory: newInventory },
        { withCredentials: true }
      );
      if (onUpdated) onUpdated();
    } catch (err) {
      toast.error('Failed to update inventory');
    }
  };

  const addItem = () => {
    const item = newItem.trim();
    if (!item) return;
    updateInventory([...inventory, item]);
    setNewItem('');
    toast.success(`Added: ${item}`);
  };

  const removeItem = (index) => {
    const removed = inventory[index];
    const updated = inventory.filter((_, i) => i !== index);
    updateInventory(updated);
    toast.success(`Removed: ${typeof removed === 'string' ? removed : removed.name}`);
  };

  const clearAll = () => {
    if (inventory.length === 0) return;
    updateInventory([]);
    toast.success('Inventory cleared');
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-blue-400 font-mono" data-testid="inventory-title">Inventory</h3>
        {inventory.length > 0 && (
          <button
            onClick={clearAll}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded text-red-400/70 hover:text-red-400 hover:bg-red-500/10 transition-all border border-transparent hover:border-red-500/20"
            data-testid="inventory-clear-all"
          >
            <Trash2 className="h-3 w-3" />
            Clear All
          </button>
        )}
      </div>

      {/* Add Item Input */}
      <div className="flex gap-2" data-testid="inventory-add-section">
        <input
          type="text"
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addItem()}
          placeholder="Add item (e.g. DC-17m Blaster Rifle)"
          className="flex-1 bg-slate-950/50 border border-white/10 focus:border-blue-400/50 text-slate-200 rounded-md px-3 py-2 text-xs placeholder:text-slate-600 focus:outline-none"
          data-testid="inventory-add-input"
        />
        <button
          onClick={addItem}
          disabled={!newItem.trim()}
          className="px-3 py-2 rounded-md text-xs font-medium transition-all flex items-center gap-1 bg-blue-500/20 border border-blue-500/30 text-blue-300 hover:bg-blue-500/30 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="inventory-add-btn"
        >
          <Plus className="h-3.5 w-3.5" />
          Add
        </button>
      </div>

      {/* Item Count */}
      {inventory.length > 0 && (
        <p className="text-xs text-slate-600 font-mono">{inventory.length} item{inventory.length !== 1 ? 's' : ''}</p>
      )}

      {/* Item List */}
      {inventory.length > 0 ? (
        <div className="space-y-1.5">
          {inventory.map((item, i) => {
            const name = typeof item === 'string' ? item : item.name;
            const desc = typeof item === 'object' ? item.description : null;
            return (
              <div
                key={`${name}-${i}`}
                className="glass-card p-2 rounded border border-white/5 text-xs flex items-start justify-between gap-2 group hover:border-white/10 transition-all"
                data-testid={`inventory-item-${i}`}
              >
                <div className="flex-1 min-w-0">
                  <span className="text-blue-300 font-medium">{name}</span>
                  {desc && <p className="text-slate-400 mt-0.5">{desc}</p>}
                </div>
                <button
                  onClick={() => removeItem(i)}
                  className="p-1 rounded text-slate-600 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
                  title="Remove item"
                  data-testid={`inventory-delete-${i}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-slate-500 text-sm text-center py-6">No items. Use the input above to add gear.</p>
      )}
    </div>
  );
};
